import { linkToSignOut } from "$routes/_lib/links";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@material-ui/core";
import { ExitToApp } from "@material-ui/icons";
import { Link } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { selectActiveProgram, useMyPrograms } from "../_lib/use-active-program";
import {
  useDefaultDevice,
  useSetDefaultDevice,
} from "../_lib/use-default-device";
import { useEnabledDevices } from "../_lib/use-enabled-devices";
import { useEpocusUserSettingsQuery } from "./_lib/__generated__/epocus-settings-operations.gql";
import { AdministrationSection } from "./_lib/administration-section";
import { ConsentCard } from "./_lib/consent-card";
import { DemoDataCard } from "./_lib/demo-data-card";
import { IdentityHeader } from "./_lib/identity-header";
import { PersonalInfoCard } from "./_lib/personal-info-card";
import { ProbeAssociationsCard } from "./_lib/probe-associations-card";
import { ProgramAeTitleCard } from "./_lib/program-ae-title-card";
import {
  SettingsCard,
  SettingsCardLabel,
  SettingsCardLabelRow,
  SettingsGroupHeading,
  SettingsScopeChip,
} from "./_lib/settings-card";
import { useCanManageProgramUsers } from "./_lib/use-can-manage-program-users";
import { useCanSeedDemoData } from "./_lib/use-can-seed-demo-data";

const NO_DEFAULT_VALUE = "__none__";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const Footer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 8,
});

const DeviceDefaultCard: FC = () => {
  const defaultDeviceId = useDefaultDevice();
  const setDefaultDevice = useSetDefaultDevice();
  const deviceOptions = useEnabledDevices();

  // A stale stored id has no matching MenuItem, which MUI renders as a blank
  // Select; show "No default" instead so the user can re-pick.
  const selectedValue =
    defaultDeviceId &&
    deviceOptions.some((device) => device.id === defaultDeviceId)
      ? defaultDeviceId
      : NO_DEFAULT_VALUE;

  return (
    <SettingsCard>
      <SettingsCardLabelRow>
        <SettingsCardLabel>Default device</SettingsCardLabel>
        <SettingsScopeChip>This browser only</SettingsScopeChip>
      </SettingsCardLabelRow>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        Pre-fills the device on New Encounter so you don't have to pick it each
        time.
      </Typography>
      <Select
        fullWidth
        variant="outlined"
        margin="dense"
        value={selectedValue}
        onChange={(e) => {
          const value = String(e.target.value);
          setDefaultDevice(value === NO_DEFAULT_VALUE ? null : value);
        }}
        inputProps={{ "aria-label": "Default device" }}
      >
        <MenuItem value={NO_DEFAULT_VALUE}>No default</MenuItem>
        {deviceOptions.map((device) => (
          <MenuItem key={device.id} value={device.id}>
            {device.label}
          </MenuItem>
        ))}
      </Select>
    </SettingsCard>
  );
};

export const EpocusSettingsPage: FC = () => {
  const { data, loading } = useEpocusUserSettingsQuery();
  const myUser = data?.myUser;
  const person = myUser?.person;
  const canManageUsers = useCanManageProgramUsers();
  const canSeedDemoData = useCanSeedDemoData();

  const { programs } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const aeTitle = program?.dimseAeTitle;
  const showProgramGroup = !!aeTitle || canManageUsers;

  return (
    <PageRoot>
      <Box>
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            fontSize: rem(24),
            lineHeight: 1.2,
          }}
        >
          Settings
        </Typography>
      </Box>

      <IdentityHeader
        firstname={person?.firstname}
        lastname={person?.lastname}
        programName={program?.name}
      />

      <SettingsGroupHeading scope="Follows you on any device">
        You
      </SettingsGroupHeading>
      {loading && !myUser ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading your details" />
        </LoadingRow>
      ) : myUser ? (
        <>
          <PersonalInfoCard
            userId={myUser._id}
            firstname={person?.firstname}
            middlename={person?.middlename}
            lastname={person?.lastname}
          />
          <ConsentCard
            userId={myUser._id}
            whatsappConsent={person?.consent?.whatsapp ?? false}
            marketing={person?.consent?.marketing ?? false}
            telephone={person?.telephone}
          />
        </>
      ) : (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            We couldn't load your account details. Please try again later.
          </Typography>
        </SettingsCard>
      )}

      <SettingsGroupHeading scope="What you scan with">
        Your setup
      </SettingsGroupHeading>
      <ProbeAssociationsCard />
      <DeviceDefaultCard />

      {showProgramGroup && (
        <>
          <SettingsGroupHeading scope={program?.name ?? undefined}>
            Your program
          </SettingsGroupHeading>
          {aeTitle && <ProgramAeTitleCard aeTitle={aeTitle} />}
          {canManageUsers && <AdministrationSection />}
        </>
      )}

      <Footer>
        <Button
          component={Link}
          to={linkToSignOut()}
          variant="outlined"
          color="primary"
          startIcon={<ExitToApp />}
          style={{ alignSelf: "flex-start" }}
        >
          Log out
        </Button>
        {canSeedDemoData && <DemoDataCard />}
      </Footer>
    </PageRoot>
  );
};
